/**
 * ScorecardAllPlayers.jsx
 * Full-field scorecard view for Admin > Reports > Scorecard Report.
 * Shows all confirmed players for a selected tee time in a single grid.
 * Read-only — score entry happens in the Scoring tab.
 *
 * Features:
 *  - Gross / Net / Putts view modes
 *  - Alphabetical / Score (leaderboard) sort
 *  - Par row pinned at top
 *  - DNF handling (incomplete rounds shown with asterisk + "thru X")
 *  - Total-score-only players (show "T" badge, hole cells show —)
 *  - Mixed scoring modes in the same round
 *  - Score coloring: eagle/birdie/par/bogey/double+
 *  - Print-optimized CSS (@media print)
 *  - Course + date in header
 *
 * Created: March 2026 — Session 5: All-Players Scorecard
 */

import React, { useState, useEffect, useCallback } from 'react';

// ─── Constants ──────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Sync token getter (impersonation-aware)
function getAuthToken() {
  return sessionStorage.getItem('impersonationToken') || localStorage.getItem('accessToken');
}

// Parse JWT expiry
function parseJwtExpiry(token) {
  try { return JSON.parse(atob(token.split('.')[1])).exp * 1000; } catch { return null; }
}

function isTokenExpired(token) {
  if (!token) return true;
  const exp = parseJwtExpiry(token);
  return !exp || Date.now() >= exp - 60000;
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    return data.accessToken;
  } catch { return null; }
}

// Async token getter — refreshes if expired
async function getValidAuthToken() {
  const imp = sessionStorage.getItem('impersonationToken');
  if (imp && !isTokenExpired(imp)) return imp;
  const token = localStorage.getItem('accessToken');
  if (isTokenExpired(token)) return await refreshAccessToken();
  return token;
}

// NET IS NO LONGER COMPUTED HERE.
//
// This file used to carry private copies of courseHcp() and strokesOnHole().
// They predated handicapCalc.js and knew nothing about competition_hole_set, so a
// nine-hole round got an EIGHTEEN-hole course handicap allocated across raw 1..18
// stroke indexes — a number the leaderboard flatly contradicted (teetime f3d5fe23:
// board 30, report 36 for the same player).
//
// Net now arrives from GET /scoring/teetimes/:id/scorecard as `net_by_player`,
// computed server-side through handicapCalc — the single source of truth, verified
// 13,787/13,788 against GHIN's published course handicaps. Do not reintroduce a
// local copy: the whole failure mode here was a duplicate that drifted.

function getTotal(arr) {
  const valid = arr.filter(x => x != null && !isNaN(x));
  return valid.length ? valid.reduce((a, b) => a + b, 0) : null;
}

function getThru(arr) {
  return arr.filter(x => x != null && !isNaN(x)).length;
}

function toPlusMinus(score, par) {
  if (score == null) return '—';
  const d = score - par;
  if (d === 0) return 'E';
  return d > 0 ? `+${d}` : `${d}`;
}

function scoreColor(score, par) {
  if (score == null || !par) return {};
  const d = score - par;
  if (d <= -2) return { background: '#fbbf24', color: '#000', fontWeight: 700, borderRadius: 3 };   // eagle
  if (d === -1) return { background: '#dc2626', color: '#fff', fontWeight: 700, borderRadius: '50%' }; // birdie
  if (d === 0)  return { fontWeight: 600 };                                                            // par
  if (d === 1)  return { background: '#3b82f6', color: '#fff', borderRadius: 3 };                     // bogey
  return { background: '#1e3a5f', color: '#fff', borderRadius: 3 };                                   // double+
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.split('T')[0] + 'T12:00:00');
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────
const ScorecardAllPlayers = ({ teetimeId, teetimeIds, teetimeMeta }) => {
  // Accept either a single teetimeId (legacy / Scoring-tab hand-off) or an array
  // of teetimeIds (multi-tee-time league round). Normalize to a stable array.
  const idList = React.useMemo(() => {
    const raw = Array.isArray(teetimeIds) ? teetimeIds : (teetimeId ? [teetimeId] : []);
    return raw.filter(Boolean);
  }, [teetimeIds, teetimeId]);
  const idKey = idList.join(',');           // stable dependency for effects
  const isMerged = idList.length > 1;
  const primaryId = idList[0] || null;      // anchor id (email route takes idList too)

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Control axes (v53.7 — four-group control bar):
  //  presentation = how it looks: 'scorecard' (colored grid) | 'report' (plain text)
  //  detail       = granularity:  'hole' (all 18 holes) | 'round' (totals only)
  //  numbers      = which numbers: 'gross' | 'net' | 'putts' | 'all' (all three stacked/columns)
  // 'numbers' === 'all' replaces the former fullSummary boolean.
  const [presentation, setPresentation] = useState('scorecard');
  const [detail, setDetail] = useState('hole');
  const [numbers, setNumbers] = useState('gross');
  const [sortMode, setSortMode] = useState('alpha');
  const [showEmail, setShowEmail] = useState(false);
  const [emailIds, setEmailIds] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  // Organizers are often not playing in the round they run, so they can't be a
  // recipient — recipients must be confirmed players with an email on file.
  const [emailCopySelf, setEmailCopySelf] = useState(false);
  // Which grids to send. Defaults to whatever the SHOW toggle is set to when the
  // modal opens — the email used to send Gross + Net + Putts regardless, so a league
  // that doesn't use net had no way to leave it out.
  const [emailSections, setEmailSections] = useState(['gross']);
  const [emailResult, setEmailResult] = useState(null);

  // Auth headers — async to support token refresh on expiry
  const getHeaders = useCallback(async () => {
    const h = { 'Content-Type': 'application/json' };
    const t = await getValidAuthToken(); // AUTO-REFRESH FIX: was getAuthToken()
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  }, []);

  const sendScorecardEmail = async () => {
    if (!primaryId || (emailIds.length === 0 && !emailCopySelf) || emailSections.length === 0) return;
    try {
      setEmailSending(true); setEmailResult(null);
      const resp = await fetch(`${API_URL}/scoring/teetimes/${primaryId}/email-scorecard`, {
        method: 'POST', credentials: 'include', headers: await getHeaders(),
        // Send EVERY tee time in the round, not just the anchor. A league round is
        // several tee times merged on screen; the email must cover the same field.
        body: JSON.stringify({ player_ids: emailIds, teetime_ids: idList,
          send_to_me: emailCopySelf, sections: emailSections })
      });
      const result = await resp.json();
      if (resp.ok) setEmailResult({ ok: true, msg: `Sent ${result.sent} email${result.sent !== 1 ? 's' : ''}: ${result.recipients.join(', ')}` });
      else setEmailResult({ ok: false, msg: result.error || 'Failed to send' });
    } catch { setEmailResult({ ok: false, msg: 'Network error — please try again' }); }
    finally { setEmailSending(false); }
  };

  // Load + merge full-field scorecard(s). For a single tee time this is identical
  // to the old behavior (a "merge of one"). For multiple, fetch each in parallel
  // and combine into one data object with the SAME shape the single fetch returns,
  // so every downstream render path (grid, summary, CSV, print) works unchanged.
  useEffect(() => {
    if (idList.length === 0) { setData(null); return; }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const headers = await getHeaders();
        const results = await Promise.all(idList.map(async (id) => {
          const resp = await fetch(`${API_URL}/scoring/teetimes/${id}/scorecard`, {
            credentials: 'include', headers
          });
          if (!resp.ok) {
            const e = await resp.json().catch(() => ({}));
            throw new Error(e.error || 'Failed to load scorecard');
          }
          const d = await resp.json();
          return { id, d };
        }));
        if (cancelled) return;

        // Single tee time → use as-is (preserves exact legacy behavior).
        if (results.length === 1) {
          setData(results[0].d);
          return;
        }

        // ── Merge multiple tee times into one combined round ──────────────
        const first = results[0].d;
        const mergedPlayers = [];
        const seen = {};                // player id -> merged player object
        const mergedScores = {};        // player id -> { hole: {...} }
        const mergedTeeByPlayer = {};
        const mergedCourseHcp = {};
        // Net is computed PER TEE TIME on the server (each carries its own
        // competition_hole_set), so it merges per player exactly like scores do.
        // Omitting it here is why every net cell dashed on a multi-tee-time round
        // while gross rendered fine — gross rides on `scores`, which was merged.
        const mergedNetByPlayer = {};
        const teesById = {};

        results.forEach(({ id: ttId, d }) => {
          const ttTime = d.tee_time?.time || '';
          (d.tees || []).forEach(t => { if (t && !teesById[t.id]) teesById[t.id] = t; });
          (d.players || []).forEach(p => {
            if (!seen[p.id]) {
              // First time we see this player: stamp their source tee time.
              const merged = { ...p, source_teetime_id: ttId, source_teetime_time: ttTime };
              seen[p.id] = merged;
              mergedPlayers.push(merged);
              if (d.scores?.[p.id]) mergedScores[p.id] = d.scores[p.id];
              if (d.tee_by_player?.[p.id]) mergedTeeByPlayer[p.id] = d.tee_by_player[p.id];
              if (d.course_hcp_by_player?.[p.id] != null) mergedCourseHcp[p.id] = d.course_hcp_by_player[p.id];
              if (d.net_by_player?.[p.id]) mergedNetByPlayer[p.id] = d.net_by_player[p.id];
            } else {
              // Player appears in 2+ selected tee times. Dedup by id; flag only if
              // the second occurrence actually carries scores (a real conflict).
              const hasScores = d.scores?.[p.id] && Object.keys(d.scores[p.id]).length > 0;
              if (hasScores) seen[p.id].conflict = true;
            }
          });
        });

        const mergedData = {
          tee_time: {
            ...first.tee_time,
            time: null,                       // ambiguous across tee times
            teetime_count: results.length,
            is_merged: true
          },
          players: mergedPlayers,
          tees: Object.values(teesById),
          default_tee: first.default_tee,
          scores: mergedScores,
          tee_by_player: mergedTeeByPlayer,
          course_hcp_by_player: mergedCourseHcp,
          net_by_player: mergedNetByPlayer,
          // Taken from the first tee time. Chained tee times in one round share a
          // hole set in practice; if they ever diverge, each player's NET is still
          // correct (it was computed against their own tee time) but the grey
          // out-of-set marking would follow the first tee time's set for everyone.
          competition_hole_set: first.competition_hole_set || 'all18'
        };
        setData(mergedData);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [idKey, getHeaders]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (idList.length === 0) return null;
  if (loading) return <div style={styles.emptyState}>Loading scorecard...</div>;
  if (error) return <div style={{ ...styles.emptyState, color: '#dc2626' }}>⚠️ {error}</div>;
  if (!data) return null;
  if (data.players.length === 0) return (
    <div style={styles.emptyState}>No confirmed players found for this tee time.</div>
  );

  // Use default tee for par row display only — net is calculated per-player
  const activeTee = data.default_tee || data.tees.find(t => t.holes?.length > 0) || data.tees[0];
  const holeData = activeTee?.holes || [];
  const hasHoleData = holeData.length > 0;

  // Course par info (for par row display)
  const coursePar = hasHoleData ? holeData.map(h => h.par) : [];
  const frontPar = hasHoleData ? getTotal(coursePar.slice(0, 9)) : null;
  const backPar  = hasHoleData ? getTotal(coursePar.slice(9))    : null;
  const totalPar = hasHoleData ? frontPar + backPar              : (activeTee?.par || null);

  // Which holes this round actually competes over. A designated nine still lets a
  // player carry on and post 18 to GHIN, but those extra holes are not part of the
  // competition — they carry no net and are marked in the grid below.
  const holeSet = data.competition_hole_set || 'all18';
  const inSetHole = (holeNum) => {
    if (holeSet === 'front9') return holeNum <= 9;
    if (holeSet === 'back9') return holeNum >= 10;
    return true;
  };
  const holeSetLabel = holeSet === 'front9' ? 'front 9' : (holeSet === 'back9' ? 'back 9' : null);

  // Check whether any player has putts data
  const hasPuttsData = data.players.some(p => {
    const ps = data.scores[p.id] || {};
    return Object.values(ps).some(s => s.putts != null);
  });

  // ── Build display rows ───────────────────────────────────────────────────
  const buildRows = (mode) => data.players.map(player => {
    const rawScores = data.scores[player.id] || {};

    const grossArr = Array.from({ length: 18 }, (_, i) => rawScores[i + 1]?.gross_score ?? null);
    const puttsArr = Array.from({ length: 18 }, (_, i) => rawScores[i + 1]?.putts ?? null);
    const holesScored = grossArr.filter(x => x != null).length;

    // Net arrives precomputed and hole-set aware (see the note at the top of this
    // file). `holes` covers scored holes INSIDE the competition set only; anything
    // absent has no net and renders as a dash.
    const netInfo = data.net_by_player?.[player.id] || null;
    const netHoles = netInfo?.holes || {};
    // Hole-set-aware course handicap from handicapCalc. On a nine this is roughly
    // HALF the stored round_scores.course_hcp, which is an 18-hole value frozen at
    // scoring time and is display-only now.
    const chcp = netInfo ? netInfo.course_hcp : null;
    const netUnavailable = netInfo ? netInfo.unavailable_reason : null;

    const recordedTeeId = data.tee_by_player?.[player.id];
    // matchedTee: a tee we can actually attribute to this player —
    //   1. the tee recorded during live scoring (course_tee_id on round_scores), or
    //   2. their preferred tee matched to THIS course's tees by id, or
    //   3. their preferred tee matched by NAME (preferred_tee_name handles the
    //      cross-course case where the stored UUID belongs to another course).
    // If none match, matchedTee is null — the player gets NO tee badge rather than
    // being mislabeled with the default tee.
    const prefName = player.preferred_tee_name || player.preferred_tee;
    const matchedTee = (recordedTeeId && data.tees.find(t => t.id === recordedTeeId))
      || (player.preferred_tee && data.tees.find(t => t.id === player.preferred_tee))
      || (prefName && data.tees.find(t => t.tee_name?.toLowerCase() === String(prefName).toLowerCase()))
      || (prefName && data.tees.find(t => t.tee_name?.toLowerCase().includes(String(prefName).toLowerCase())))
      || null;
    // calcTee: used only for net stroke allocation / hole data. Falls back to the
    // course default tee so net can still be computed, but this is NOT shown as a badge.
    const playerTee = matchedTee || activeTee;

    const playerHoleData = playerTee?.holes?.length > 0 ? playerTee.holes : holeData;

    let displayArr;
    if (mode === 'putts') {
      displayArr = puttsArr;
    } else if (mode === 'net') {
      // Keyed by hole NUMBER, not array position. Absent => outside the competition
      // set, or unscored, or net unavailable — all three render the same dash.
      displayArr = grossArr.map((g, i) => (g == null ? null : (netHoles[i + 1] ?? null)));
    } else {
      displayArr = grossArr;
    }

    const front = getTotal(displayArr.slice(0, 9));
    const back  = getTotal(displayArr.slice(9));
    const total = (front != null || back != null) ? (front ?? 0) + (back ?? 0) : null;
    const thru = mode === 'putts' ? getThru(puttsArr) : getThru(grossArr);
    const dnf = holesScored > 0 && holesScored < 18;

    // Which holes count for THIS competition. Used only to mark the grid — the
    // numbers above need no hole-set logic, because net is already absent outside
    // the set and the dash rule does the rest.
    const inSetArr = Array.from({ length: 18 }, (_, i) => inSetHole(i + 1));

    // PAR BASELINE FOR +/-. This used to compare every row against the full 18-hole
    // par no matter how many holes were played — an eleven-hole round read 48 against
    // par 70 and reported -22. The baseline must be the par of the holes THIS row
    // actually counts, which differs by mode: gross counts every hole played, net
    // counts in-set holes only. Two baselines, each correct for what it measures.
    let rowPar = null;
    if (mode !== 'putts') {
      let sum = 0, any = false;
      displayArr.forEach((v, i) => {
        if (v == null) return;
        const hd = playerHoleData.find(h => h.hole_number === i + 1);
        if (hd?.par != null) { sum += hd.par; any = true; }
      });
      rowPar = any ? sum : null;
    }

    return { ...player, grossArr, puttsArr, displayArr, front, back, total, thru, dnf,
             chcp, holesScored, playerTee, matchedTee, inSetArr, rowPar, netUnavailable,
             outOfSetScored: inSetArr.some((inSet, i) => !inSet && grossArr[i] != null) };
  });

  const buildSorted = (mode) => [...buildRows(mode)].sort((a, b) => {
    if (sortMode === 'alpha') return a.name.localeCompare(b.name);
    const ta = (a.dnf || a.total == null) ? 9999 : a.total;
    const tb = (b.dnf || b.total == null) ? 9999 : b.total;
    if (ta !== tb) return ta - tb;
    return (a.front ?? 9999) - (b.front ?? 9999);
  });

  // The single mode used when not showing all three. 'all' falls back to gross
  // for score-sorting and for the primary grid.
  const primaryMode = numbers === 'all' ? 'gross' : numbers;
  const sorted = buildSorted(primaryMode);

  // ── CSV export ─────────────────────────────────────────────────────────────
  // Two shapes, chosen by the Detail axis:
  //  By round → summary (one row per player: identity + Gross/Net/Putts totals)
  //  By hole  → wide hole-by-hole (one row per player: identity + 18 gross + OUT/IN/TOT
  //             + Net total + Putts total)
  // Every row self-identifies (course, date, tee) so files survive being merged.
  const csvEscape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const triggerDownload = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const downloadCsv = () => {
    if (!data) return;
    const courseName = data.tee_time.course_name || '';
    const dateStr = (data.tee_time.date || '').split('T')[0];
    const rows = [];
    const grossRows = buildSorted('gross');
    const netById = {}; buildRows('net').forEach(r => { netById[r.id] = r.total; });
    const puttsById = {}; buildRows('putts').forEach(r => { puttsById[r.id] = r.total; });

    // Friendly group identifier. Shotgun orgs: starting_hole + group_label ("1A","10C").
    // Sequential/non-shotgun: fall back to the pairing_group number. Else blank.
    const groupOf = (p) => {
      const lbl = p.group_label;
      if (p.starting_hole != null && lbl != null && String(lbl).trim() !== '') return `${p.starting_hole}${lbl}`;
      if (p.pairing_group != null) return String(p.pairing_group);
      return '';
    };

    if (detail === 'round') {
      rows.push(['course', 'date', 'tee_time', 'tee', 'start_hole', 'grp_label', 'pairing', 'group', 'player', 'hcp_index', 'course_hcp', 'gross', 'net', 'putts']);
      grossRows.forEach(p => {
        rows.push([
          courseName, dateStr, p.source_teetime_time ?? (data.tee_time.time || ''), p.matchedTee?.tee_name || '',
          p.starting_hole ?? '', p.group_label ?? '', p.pairing_group ?? '', groupOf(p),
          p.name, p.handicap ?? '', p.chcp ?? '', p.total ?? '', netById[p.id] ?? '', puttsById[p.id] ?? ''
        ]);
      });
    } else {
      const holeCols = Array.from({ length: 18 }, (_, i) => `h${i + 1}`);
      rows.push(['course', 'date', 'tee_time', 'tee', 'start_hole', 'grp_label', 'pairing', 'group', 'player', 'hcp_index', 'course_hcp', ...holeCols, 'out', 'in', 'gross', 'net', 'putts']);
      grossRows.forEach(p => {
        rows.push([
          courseName, dateStr, p.source_teetime_time ?? (data.tee_time.time || ''), p.matchedTee?.tee_name || '',
          p.starting_hole ?? '', p.group_label ?? '', p.pairing_group ?? '', groupOf(p),
          p.name, p.handicap ?? '', p.chcp ?? '',
          ...p.grossArr.map(s => s ?? ''),
          p.front ?? '', p.back ?? '', p.total ?? '', netById[p.id] ?? '', puttsById[p.id] ?? ''
        ]);
      });
    }
    const csv = rows.map(r => r.map(csvEscape).join(',')).join('\r\n');
    const safeCourse = (courseName || 'scorecard').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    const shape = detail === 'round' ? 'summary' : 'holes';
    triggerDownload(csv, `${safeCourse}_${dateStr || 'round'}_${shape}.csv`);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={styles.root} className="scorecard-all-root">
      <PrintStyles />

      {/* ── Header Bar ─────────────────────────────────────────────────── */}
      <div style={styles.header} className="sc-header">
        <div style={styles.headerTop}>
          <div>
            <div style={styles.courseName}>{data.tee_time.course_name}</div>
            <div style={styles.meta}>
              {formatDate(data.tee_time.date)}
              {data.tee_time.is_merged
                ? ` · ${data.tee_time.teetime_count} tee times`
                : (data.tee_time.time ? ` · ${data.tee_time.time}` : '')}
              {(() => {
                const usedTeeIds = new Set(Object.values(data.tee_by_player || {}));
                if (usedTeeIds.size > 1) return ' · Mixed Tees';
                if (usedTeeIds.size === 1) {
                  const t = data.tees.find(t => t.id === [...usedTeeIds][0]);
                  if (t) return ` · ${t.tee_name} Tees`;
                }
                return ''; // no tees recorded yet
              })()}
              {activeTee ? ` · Par ${totalPar}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }} className="no-print">
            <button style={styles.printBtn} onClick={() => window.print()}>🖨️ Print</button>
            {/* Available on merged rounds too. This was gated on !isMerged because the
                email endpoint only ever read the anchor tee time — sending would have
                covered a fraction of the field. The endpoint now takes the whole
                teetime_ids list, so the guard is obsolete. */}
            <button style={{ ...styles.printBtn, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }}
              onClick={() => {
                setEmailIds(data.players.map(p => p.id));
                // Seed from the on-screen choice: what you're looking at is what you send.
                setEmailSections(numbers === 'all'
                  ? ['gross', 'net', ...(hasPuttsData ? ['putts'] : [])]
                  : [numbers]);
                setShowEmail(true); setEmailResult(null);
              }}>
              ✉️ Email
            </button>
            <button style={{ ...styles.printBtn, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}
              onClick={downloadCsv}>
              ⬇️ Download CSV
            </button>
          </div>
        </div>

        {/* Controls row — four axes (v53.7) */}
        <div style={styles.controlsRow} className="no-print">
          {/* View — presentation: colored grid vs plain text */}
          <div style={styles.controlGroup}>
            <span style={styles.controlLabel}>View</span>
            <ToggleBtn active={presentation === 'scorecard'} onClick={() => setPresentation('scorecard')}>Scorecard</ToggleBtn>
            <ToggleBtn active={presentation === 'report'} onClick={() => setPresentation('report')}>Report</ToggleBtn>
          </div>
          {/* Detail — granularity: every hole vs round totals */}
          <div style={styles.controlGroup}>
            <span style={styles.controlLabel}>Detail</span>
            <ToggleBtn active={detail === 'hole'} onClick={() => setDetail('hole')}>By hole</ToggleBtn>
            <ToggleBtn active={detail === 'round'} onClick={() => setDetail('round')}>By round</ToggleBtn>
          </div>
          {/* Show — which numbers */}
          <div style={styles.controlGroup}>
            <span style={styles.controlLabel}>Show</span>
            <ToggleBtn active={numbers === 'gross'} onClick={() => setNumbers('gross')}>Gross</ToggleBtn>
            <ToggleBtn active={numbers === 'net'}   onClick={() => setNumbers('net')}>Net</ToggleBtn>
            <ToggleBtn
              active={numbers === 'putts'}
              onClick={() => { if (hasPuttsData) setNumbers('putts'); }}
              disabled={!hasPuttsData}
              title={hasPuttsData ? undefined : 'No putts data for this round'}
            >
              Putts
            </ToggleBtn>
            <ToggleBtn active={numbers === 'all'} onClick={() => setNumbers('all')}>All</ToggleBtn>
          </div>
          {/* Sort */}
          <div style={styles.controlGroup}>
            <span style={styles.controlLabel}>Sort</span>
            <ToggleBtn active={sortMode === 'alpha'} onClick={() => setSortMode('alpha')}>A-Z</ToggleBtn>
            <ToggleBtn active={sortMode === 'score'} onClick={() => setSortMode('score')}>Score</ToggleBtn>
          </div>
          <span style={styles.playerCount}>{data.players.length} PLAYERS</span>
        </div>
      </div>

      {/* ── Grid ───────────────────────────────────────────────────────── */}
      {(() => {
        const plain = presentation === 'report';
        const renderGrid = (mode, rows) => {
          const isPutts = mode === 'putts';
          const totPar = totalPar;
          return (
            <div style={styles.scrollWrapper} key={mode}>
              <table style={styles.table} className="sc-table">
                <thead>
                  <tr>
                    <th style={styles.thPlayer}>PLAYER</th>
                    {[1,2,3,4,5,6,7,8,9].map(h => <th key={h} style={styles.thHole}>{h}</th>)}
                    <th style={styles.thNine}>OUT</th>
                    <th style={styles.thGap}></th>
                    {[10,11,12,13,14,15,16,17,18].map(h => <th key={h} style={styles.thHole}>{h}</th>)}
                    <th style={styles.thNine}>IN</th>
                    <th style={isPutts ? { ...styles.thTot, background: '#0f766e' } : styles.thTot}>TOT</th>
                    <th style={isPutts ? { ...styles.thPm, background: '#0f766e' } : styles.thPm}>{isPutts ? 'PUTTS' : '+/-'}</th>
                  </tr>
                </thead>
                <tbody>
                  {!isPutts && (
                    <>
                      <tr style={styles.parRow}>
                        <td style={styles.parLabel}>PAR</td>
                        {[0,1,2,3,4,5,6,7,8].map(i => <td key={i} style={styles.parCell}>{coursePar[i] || '—'}</td>)}
                        <td style={styles.parNine}>{frontPar || '—'}</td>
                        <td style={styles.tdGap}></td>
                        {[9,10,11,12,13,14,15,16,17].map(i => <td key={i} style={styles.parCell}>{coursePar[i] || '—'}</td>)}
                        <td style={styles.parNine}>{backPar || '—'}</td>
                        <td style={styles.parTot}>{totPar || '—'}</td>
                        <td style={styles.parCell}></td>
                      </tr>
                      {hasHoleData && (
                        <tr style={{ background: '#f0f4f8' }}>
                          <td style={{ ...styles.parLabel, color: '#6b7280', fontSize: 10, fontWeight: 600 }}>HCP</td>
                          {[0,1,2,3,4,5,6,7,8].map(i => (
                            <td key={i} style={{ ...styles.parCell, color: '#6b7280', fontSize: 10 }}>
                              {holeData.find(h => h.hole_number === i + 1)?.handicap_index || '-'}
                            </td>
                          ))}
                          <td style={styles.tdGap}></td>
                          <td style={styles.tdGap}></td>
                          {[9,10,11,12,13,14,15,16,17].map(i => (
                            <td key={i} style={{ ...styles.parCell, color: '#6b7280', fontSize: 10 }}>
                              {holeData.find(h => h.hole_number === i + 1)?.handicap_index || '-'}
                            </td>
                          ))}
                          <td style={styles.tdGap}></td>
                          <td style={styles.tdGap}></td>
                          <td style={styles.tdGap}></td>
                        </tr>
                      )}
                    </>
                  )}
                  {rows.map((p, rowIdx) => {
                    // p.rowPar, not totPar: compare against the par of the holes this
                    // row actually counts (see buildRows). totPar remains the header's
                    // course par and is still shown on the PAR row above.
                    const totPm = (!isPutts && p.total != null && p.rowPar) ? toPlusMinus(p.total, p.rowPar) : null;
                    const rowBg = rowIdx % 2 === 0 ? '#fff' : '#f9fafb';
                    return (
                      <tr key={p.id} style={{ background: rowBg }}>
                        <td style={styles.tdName}>
                          <div style={styles.nameInner}>
                            <span style={styles.playerName}>{p.name}</span>
                            {p.handicap != null && <span style={styles.hcpBadge}>HCP {p.handicap}</span>}
                            {p.matchedTee?.tee_name && (
                              <span style={{ ...styles.hcpBadge,
                                background: p.matchedTee.tee_color || '#e5e7eb',
                                color: ['#fff','white','#ffffff'].includes((p.matchedTee.tee_color||'').toLowerCase()) ? '#374151' : '#fff',
                                border: '1px solid rgba(0,0,0,0.1)' }}>
                                {p.matchedTee.tee_name}
                              </span>
                            )}
                            {p.dnf && <span style={styles.dnfBadge}>THRU {p.thru}</span>}
                            {p.conflict && <span style={{ ...styles.dnfBadge, background: '#dc2626' }} title="This player appears in more than one selected tee time">⚠ 2 tee times</span>}
                          </div>
                        </td>
                        {p.displayArr.slice(0, 9).map((s, i) => {
                          const par = hasHoleData ? holeData.find(h => h.hole_number === i + 1)?.par : null;
                          // Outside the competition set: no performance colour, muted
                          // instead. The colour legend means "vs par in the competition",
                          // and these holes aren't in it — the gross total counts them,
                          // the net total doesn't, and the reader needs to see why.
                          const outOfSet = !p.inSetArr[i];
                          const cs = outOfSet ? styles.cellOutOfSet
                            : (plain ? {} : (isPutts ? (s != null ? (s >= 3 ? styles.cellPuttsHi : styles.cellPutts) : styles.cellEmpty)
                            : (s != null && par ? scoreColor(mode === 'gross' ? p.grossArr[i] : s, par) : {})));
                          return <td key={i} style={{ ...styles.tdHole, ...cs }}>{s != null ? s : <span style={styles.dash}>—</span>}</td>;
                        })}
                        <td style={styles.tdNine}>{p.front != null ? p.front : <span style={styles.dash}>—</span>}</td>
                        <td style={styles.tdGap}></td>
                        {p.displayArr.slice(9).map((s, i) => {
                          const par = hasHoleData ? holeData.find(h => h.hole_number === i + 10)?.par : null;
                          const outOfSet = !p.inSetArr[i + 9];
                          const cs = outOfSet ? styles.cellOutOfSet
                            : (plain ? {} : (isPutts ? (s != null ? (s >= 3 ? styles.cellPuttsHi : styles.cellPutts) : styles.cellEmpty)
                            : (s != null && par ? scoreColor(mode === 'gross' ? p.grossArr[i + 9] : s, par) : {})));
                          return <td key={i} style={{ ...styles.tdHole, ...cs }}>{s != null ? s : <span style={styles.dash}>—</span>}</td>;
                        })}
                        <td style={styles.tdNine}>{p.back != null ? p.back : <span style={styles.dash}>—</span>}</td>
                        <td style={{ ...styles.tdTotal, ...(p.dnf ? styles.tdTotalDnf : {}) }}>
                          {p.total != null ? p.total : <span style={styles.dash}>—</span>}{p.dnf ? '*' : ''}
                        </td>
                        <td style={isPutts ? { ...styles.tdPm, background: '#f0fdfa', color: '#0f766e' } : styles.tdPm}>
                          {isPutts ? (
                            getTotal(p.puttsArr) != null ? <span style={{ fontWeight: 700 }}>{getTotal(p.puttsArr)}</span> : <span style={styles.dash}>—</span>
                          ) : totPm === null ? (
                            <span style={styles.dash}>—</span>
                          ) : totPm === 'E' ? (
                            <span style={styles.pmEven}>E</span>
                          ) : totPm.startsWith('+') ? (
                            <span style={styles.pmOver}>{totPm}</span>
                          ) : (
                            <span style={styles.pmUnder}>{totPm}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        // ── Summary table (Detail = By round): one row per player, totals only ──
        // Core fields only: Player, HCP, Tee, Gross, Net, Putts. Respects 'numbers':
        //  'all'  → show Gross + Net + Putts columns together
        //  single → show just that total column
        const renderSummaryTable = () => {
          const showGross = numbers === 'all' || numbers === 'gross';
          const showNet   = numbers === 'all' || numbers === 'net';
          const showPutts = numbers === 'all' || numbers === 'putts';
          // Sort by the number currently shown (gross when 'all'); buildSorted honors
          // the active sortMode (A-Z vs Score), so Score now orders by the shown number.
          const sortKey = numbers === 'all' ? 'gross' : numbers;
          const orderedRows = buildSorted(sortKey);
          // Per-player net/putts totals + course handicap, keyed by id.
          const byId = {};
          const grossById = {};
          buildRows('gross').forEach(r => { grossById[r.id] = r.total; });
          buildRows('net').forEach(r => { byId[r.id] = { ...(byId[r.id] || {}), net: r.total, chcp: r.chcp }; });
          buildRows('putts').forEach(r => { byId[r.id] = { ...(byId[r.id] || {}), putts: r.total }; });
          const headTh = { ...BASE_CELL, fontWeight: 700, color: '#fff', background: '#1f2937', minWidth: 48 };
          return (
            <div style={styles.scrollWrapper}>
              <table style={styles.table} className="sc-table">
                <thead>
                  <tr>
                    <th style={styles.thPlayer}>PLAYER</th>
                    <th style={headTh}>HCP</th>
                    <th style={headTh}>TEE</th>
                    {showGross && <th style={{ ...headTh, background: '#166534' }}>GROSS</th>}
                    {showNet   && <th style={{ ...headTh, background: '#166534' }}>NET</th>}
                    {showPutts && <th style={{ ...headTh, background: '#0f766e' }}>PUTTS</th>}
                  </tr>
                </thead>
                <tbody>
                  {orderedRows.map((p, rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? '#fff' : '#f9fafb';
                    const extra = byId[p.id] || {};
                    // Gross total is mode-independent for display; net/putts come from byId.
                    // p.total reflects the sortKey's mode, so don't read columns off it.
                    const grossTotal = grossById[p.id];
                    return (
                      <tr key={p.id} style={{ background: rowBg }}>
                        <td style={styles.tdName}>
                          <div style={styles.nameInner}>
                            <span style={styles.playerName}>{p.name}</span>
                            {p.dnf && <span style={styles.dnfBadge}>THRU {p.thru}</span>}
                            {p.conflict && <span style={{ ...styles.dnfBadge, background: '#dc2626' }} title="This player appears in more than one selected tee time">⚠ 2 tee times</span>}
                          </div>
                        </td>
                        <td style={{ ...styles.tdHole, color: '#6b7280' }}>{p.handicap != null ? p.handicap : <span style={styles.dash}>—</span>}</td>
                        <td style={{ ...styles.tdHole, color: '#6b7280' }}>{p.matchedTee?.tee_name || <span style={styles.dash}>—</span>}</td>
                        {showGross && <td style={styles.tdTotal}>{grossTotal != null ? grossTotal : <span style={styles.dash}>—</span>}{p.dnf ? '*' : ''}</td>}
                        {showNet   && <td style={styles.tdTotal}>{extra.net != null ? extra.net : <span style={styles.dash}>—</span>}</td>}
                        {showPutts && <td style={{ ...styles.tdTotal, background: '#f0fdfa', color: '#0f766e' }}>{extra.putts != null ? extra.putts : <span style={styles.dash}>—</span>}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        };

        // ── Route on the two axes ──────────────────────────────────────────
        // Detail = By round → totals summary table (color or plain text differ only
        // in that 'plain' has no effect here; the summary is the same either way).
        if (detail === 'round') {
          return renderSummaryTable();
        }
        // Detail = By hole, Show = All → stacked gross/net/putts grids (former Full Summary)
        if (numbers === 'all') {
          return ['gross', 'net', 'putts'].map((mode, idx) => (
            <div key={mode}>
              <div style={{ padding: '8px 16px 4px', fontWeight: 700, fontSize: 12, color: '#374151',
                textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb',
                borderTop: idx > 0 ? '2px solid #e5e7eb' : 'none' }}>
                Today's Round — {mode.charAt(0).toUpperCase() + mode.slice(1)}
                {mode === 'putts' && !hasPuttsData && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, textTransform: 'none', fontSize: 11 }}>No putts data recorded</span>}
              </div>
              {renderGrid(mode, buildSorted(mode))}
            </div>
          ));
        }
        // Detail = By hole, single number → one grid
        return renderGrid(primaryMode, sorted);
      })()}

      {/* ── Legend ─────────────────────────────────────────────────────── */}
      {presentation === 'scorecard' && detail === 'hole' && numbers !== 'putts' && (
        <div style={styles.legend} className="no-print">
          <LegendItem color="#fbbf24" textColor="#000">Eagle+</LegendItem>
          <LegendItem color="#dc2626" textColor="#fff" round>Birdie</LegendItem>
          <LegendItem color="transparent" textColor="#374151" border>Par</LegendItem>
          <LegendItem color="#3b82f6" textColor="#fff">Bogey</LegendItem>
          <LegendItem color="#1e3a5f" textColor="#fff">Double+</LegendItem>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>
            * = Round incomplete (thru X holes)
          </span>
        </div>
      )}

      {/* Only when someone actually played outside the competition set. On a normal
          18-hole round this never renders. Explains why the greyed holes count toward
          the gross total but not the net one — the question this report would
          otherwise generate. */}
      {/* "est" (Sept 23, 2026): nets built off ESTIMATED nine-hole ratings. The flag
          rides inside each net_by_player entry, so the multi-tee-time merge keeps it. */}
      {holeSetLabel && Object.values(data.net_by_player || {}).some(n => n && n.estimated) && (
        <div style={{ fontSize: 11, color: '#6b7280', padding: '6px 14px', lineHeight: 1.5 }}>
          <strong>est</strong> — 9-hole handicap estimated from the course's 18-hole rating; official 9-hole ratings aren't on file.
        </div>
      )}

      {holeSetLabel && detail === 'hole' && buildRows('gross').some(p => p.outOfSetScored) && (
        <div style={{ fontSize: 11, color: '#6b7280', padding: '6px 14px', lineHeight: 1.5 }}>
          Greyed holes fall outside the {holeSetLabel} and are not part of the competition.
          They count toward the gross total but carry no net, so the net total covers the
          {' '}{holeSetLabel} only.
        </div>
      )}

      {/* Print-only footer */}
      <div style={styles.printFooter} className="print-only">
        <span>{presentation === 'report' ? 'Report' : 'Scorecard'} · {detail === 'round' ? 'By round' : 'By hole'} · {numbers.charAt(0).toUpperCase() + numbers.slice(1)}</span>
        <span> · </span>
        <span>Generated by Tee Time Organizer</span>
      </div>

      {/* ── Email Modal ─────────────────────────────────────────────────── */}
      {showEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={() => setShowEmail(false)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', margin: 16 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>✉️ Email Scorecard</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
              Sends the full-field scorecard to the players you select.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb',
              borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#374151' }}>
              <span style={{ color: '#6b7280' }}>Include:</span>
              {[['gross','Gross'],['net','Net'],['putts','Putts']].map(([key,label]) => {
                const disabled = key === 'putts' && !hasPuttsData;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5,
                    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1 }}>
                    <input type="checkbox" disabled={disabled}
                      checked={emailSections.includes(key)}
                      onChange={e => setEmailSections(prev =>
                        e.target.checked ? [...prev, key] : prev.filter(s => s !== key))} />
                    {label}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button onClick={() => setEmailIds(data.players.map(p => p.id))}
                style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', background: '#f9fafb' }}>Select All</button>
              <button onClick={() => setEmailIds([])}
                style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', background: '#f9fafb' }}>None</button>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 14 }}>
              {data.players.map((p, i) => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                  <input type="checkbox" checked={emailIds.includes(p.id)}
                    onChange={e => setEmailIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))} />
                  <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{p.name}</span>
                  {!p.email && <span style={{ fontSize: 10, color: '#ef4444' }}>no email</span>}
                </label>
              ))}
            </div>
            {emailResult && (
              <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12,
                background: emailResult.ok ? '#f0fdf4' : '#fef2f2',
                color: emailResult.ok ? '#166534' : '#dc2626',
                border: `1px solid ${emailResult.ok ? '#bbf7d0' : '#fecaca'}` }}>
                {emailResult.ok ? '✅ ' : '⚠️ '}{emailResult.msg}
              </div>
            )}
            {/* The address comes from the current session server-side; we deliberately
                don't assert whose it is, because on an impersonated session it may not
                be the admin's own. The result message reports what was actually used. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151',
              marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={emailCopySelf}
                onChange={e => setEmailCopySelf(e.target.checked)} />
              Also send a copy to my own email address
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowEmail(false)}
                style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff', fontSize: 13 }}>Close</button>
              <button onClick={sendScorecardEmail} disabled={emailSending || (emailIds.length === 0 && !emailCopySelf) || emailSections.length === 0}
                style={{ padding: '7px 16px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#fff',
                  cursor: emailSending || (emailIds.length === 0 && !emailCopySelf) || emailSections.length === 0 ? 'not-allowed' : 'pointer',
                  background: emailSending || (emailIds.length === 0 && !emailCopySelf) || emailSections.length === 0 ? '#9ca3af' : '#1d4ed8' }}>
                {emailSending ? 'Sending...'
                  : emailIds.length === 0 ? 'Send a copy to me'
                  : `Send to ${emailIds.length} player${emailIds.length !== 1 ? 's' : ''}${emailCopySelf ? ' + me' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const ToggleBtn = ({ active, onClick, disabled, title, children }) => (
  <button
    onClick={disabled ? undefined : onClick}
    title={title}
    style={{
      padding: '5px 12px',
      border: active ? '2px solid #166534' : '1px solid #d1d5db',
      background: active ? '#f0fdf4' : '#fff',
      color: active ? '#166534' : (disabled ? '#d1d5db' : '#374151'),
      borderRadius: 6,
      fontSize: 12,
      fontWeight: active ? 700 : 400,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.1s'
    }}
  >
    {children}
  </button>
);

const LegendItem = ({ color, textColor, round, border, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7280' }}>
    <div style={{
      width: 18, height: 18,
      background: color,
      border: border ? '1px solid #d1d5db' : 'none',
      borderRadius: round ? '50%' : 3,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: textColor, fontSize: 10, fontWeight: 700
    }}>4</div>
    {children}
  </div>
);

// ─── Print CSS injected once ──────────────────────────────────────────────────
const PrintStyles = () => (
  <style>{`
    @media print {
      body * { visibility: hidden; }
      .scorecard-all-root, .scorecard-all-root * { visibility: visible; }
      .scorecard-all-root { position: absolute; left: 0; top: 0; width: 100%; }
      .no-print { display: none !important; }
      .print-only { display: block !important; }
      .sc-table { font-size: 10px; }
      .sc-header { padding: 8px; }
      @page { size: landscape; margin: 10mm; }
    }
    .print-only { display: none; }
  `}</style>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const BASE_CELL = {
  padding: '5px 4px',
  textAlign: 'center',
  fontSize: 12,
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap'
};

const styles = {
  root: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
    fontSize: 13
  },
  header: {
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    padding: '12px 16px 8px'
  },
  headerTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  courseName: {
    fontWeight: 700,
    fontSize: 16,
    color: '#111827'
  },
  meta: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2
  },
  printBtn: {
    padding: '6px 14px',
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    color: '#374151',
    fontWeight: 500
  },
  teeSelect: {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 12,
    background: '#fff'
  },
  controlsRow: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  controlGroup: {
    display: 'flex',
    gap: 4,
    alignItems: 'center'
  },
  controlLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginRight: 4
  },
  playerCount: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#9ca3af',
    fontWeight: 600,
    letterSpacing: '0.06em'
  },
  scrollWrapper: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 700
  },
  // Header cells
  thPlayer: {
    ...BASE_CELL,
    textAlign: 'left',
    paddingLeft: 12,
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    background: '#1f2937',
    minWidth: 140,
    position: 'sticky',
    left: 0,
    zIndex: 2
  },
  thHole: {
    ...BASE_CELL,
    fontWeight: 700,
    color: '#fff',
    background: '#1f2937',
    minWidth: 32
  },
  thNine: {
    ...BASE_CELL,
    fontWeight: 700,
    color: '#fff',
    background: '#374151',
    minWidth: 36
  },
  thTot: {
    ...BASE_CELL,
    fontWeight: 700,
    color: '#fff',
    background: '#166534',
    minWidth: 40
  },
  thPm: {
    ...BASE_CELL,
    fontWeight: 700,
    color: '#fff',
    background: '#166534',
    minWidth: 36
  },
  thNotes: {
    ...BASE_CELL,
    fontWeight: 700,
    color: '#fff',
    background: '#0f766e',
    minWidth: 48
  },
  thGap: {
    background: '#0f172a',
    width: 4,
    padding: 0
  },
  // Par row
  parRow: {
    background: '#f3f4f6'
  },
  parLabel: {
    ...BASE_CELL,
    textAlign: 'left',
    paddingLeft: 12,
    fontWeight: 700,
    fontSize: 11,
    color: '#6b7280',
    position: 'sticky',
    left: 0,
    background: '#f3f4f6',
    zIndex: 1
  },
  // Holes outside the competition set: present, readable, visibly not competing.
  // Muted rather than colour-coded, because the colour legend means "vs par in the
  // competition" and these holes are not in it.
  cellOutOfSet: { background: '#f3f4f6', color: '#9ca3af', fontStyle: 'italic' },
  parCell: {
    ...BASE_CELL,
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 600
  },
  parNine: {
    ...BASE_CELL,
    fontSize: 11,
    fontWeight: 700,
    color: '#374151',
    background: '#e5e7eb'
  },
  parTot: {
    ...BASE_CELL,
    fontSize: 11,
    fontWeight: 700,
    color: '#166534',
    background: '#dcfce7'
  },
  // Player row cells
  tdName: {
    ...BASE_CELL,
    textAlign: 'left',
    paddingLeft: 12,
    paddingRight: 8,
    position: 'sticky',
    left: 0,
    background: 'inherit',
    zIndex: 1,
    minWidth: 140
  },
  nameInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'nowrap'
  },
  playerName: {
    fontWeight: 600,
    fontSize: 12,
    color: '#111827',
    whiteSpace: 'nowrap'
  },
  hcpBadge: {
    fontSize: 10,
    color: '#9ca3af',
    background: '#f3f4f6',
    borderRadius: 3,
    padding: '1px 4px',
    whiteSpace: 'nowrap'
  },
  dnfBadge: {
    fontSize: 9,
    color: '#fff',
    background: '#f59e0b',
    borderRadius: 3,
    padding: '1px 4px',
    fontWeight: 700,
    whiteSpace: 'nowrap'
  },
  tdHole: {
    ...BASE_CELL,
    minWidth: 32
  },
  tdNine: {
    ...BASE_CELL,
    fontWeight: 700,
    background: '#f3f4f6',
    fontSize: 13
  },
  tdGap: {
    background: '#0f172a',
    width: 4,
    padding: 0
  },
  tdTotal: {
    ...BASE_CELL,
    fontWeight: 700,
    fontSize: 14,
    background: '#f0fdf4',
    color: '#166534'
  },
  tdTotalDnf: {
    color: '#f59e0b',
    background: '#fffbeb'
  },
  tdPm: {
    ...BASE_CELL,
    fontWeight: 700,
    fontSize: 12,
    background: '#f0fdf4'
  },
  tdNotes: {
    ...BASE_CELL,
    fontSize: 12,
    color: '#0f766e',
    fontWeight: 600,
    background: '#f0fdfa'
  },
  // Score type indicators
  cellPutts: {
    background: '#eff6ff',
    color: '#1d4ed8',
    fontWeight: 600
  },
  cellPuttsHi: {
    background: '#fee2e2',
    color: '#991b1b',
    fontWeight: 700
  },
  cellEmpty: {},
  // Plus/minus
  pmEven: { color: '#374151', fontWeight: 700 },
  pmOver:  { color: '#dc2626', fontWeight: 700 },
  pmUnder: { color: '#16a34a', fontWeight: 700 },
  // Legend
  legend: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '8px 16px',
    borderTop: '1px solid #e5e7eb',
    background: '#f9fafb',
    flexWrap: 'wrap'
  },
  // Misc
  dash: { color: '#d1d5db', fontSize: 10 },
  emptyState: {
    padding: 32,
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 13
  },
  printFooter: {
    padding: 8,
    textAlign: 'center',
    fontSize: 11,
    color: '#6b7280',
    borderTop: '1px solid #e5e7eb'
  }
};

export default ScorecardAllPlayers;
